import type {
  AccessibleFleetAPI,
  DefaultRolesAPI,
  UserAPI,
  UserAttributesAPI,
  UserPreferencesAPI,
} from './ApiTypes';
import type {
  UserAttributes,
  UserPreferences,
  UserPreferencesToSave,
} from './Types';
import {
  accessibleFleetsApiToAccessibeFleets,
  userAttributesTransformer,
  userPreferencesSaveForm,
  userPreferencesTransformer,
  usersApiToUsers,
} from './UserTransformer';
import type { AxiosResponse } from 'axios';

import type {
  AccessibleFleet,
  User,
  UserRole,
  UUID,
} from '@cobliteam/common-types';

import {
  type Configuration,
  type DefaultRole,
  defaultRolesTransformer,
} from '../..';
import HttpMethod from '../../request/HttpMethod';
import Requester from '../../request/Requester';
import type { IRequest } from '../../request/Types';

export class UserRequester {
  private requester: Requester;

  constructor(configuration: Configuration) {
    this.requester = new Requester(configuration);
  }

  public getFleetUsers = (fleetId: UUID): Promise<User[]> => {
    const requestData: IRequest = {
      endpoint: `/dash/fleet/${fleetId}/users`,
      method: HttpMethod.GET,
    };
    return this.requester.request(requestData).then((response) => {
      const fleetUsers: UserAPI[] = response.data;
      return usersApiToUsers(fleetUsers);
    });
  };

  public getUserAttributes = (): Promise<UserAttributes> => {
    const requestData: IRequest = {
      endpoint: '/dash/user/attributes',
      method: HttpMethod.GET,
    };
    return this.requester
      .request(requestData)
      .then((response: AxiosResponse<UserAttributesAPI>) =>
        userAttributesTransformer(response.data)
      );
  };

  public getUserAcessibleFleets = (): Promise<AccessibleFleet[]> => {
    const requestData: IRequest = {
      endpoint: '/dash/user-fleets',
      method: HttpMethod.GET,
    };
    return this.requester
      .request(requestData)
      .then((response: AxiosResponse<AccessibleFleetAPI[]>) =>
        accessibleFleetsApiToAccessibeFleets(response.data)
      );
  };

  public deleteFleetUser = (fleetId: UUID, user: User): Promise<void> => {
    const requestData: IRequest = {
      endpoint: `/account/users/${user.id}`,
      method: HttpMethod.DELETE,
    };

    return this.requester.request(requestData).then(undefined);
  };

  public saveUserPermissions = (
    userId: UUID,
    role: UserRole,
    groupsRestriction: UUID[],
    customPermissions: string[]
  ): Promise<void> => {
    const userPermissionsForm = {
      role,
      groups_restriction: groupsRestriction,
      custom_permissions: customPermissions,
    };
    const requestData: IRequest = {
      endpoint: `/account/users/${userId}/permissions`,
      method: HttpMethod.PUT,
      body: JSON.stringify(userPermissionsForm),
    };
    return this.requester.request(requestData).then(undefined);
  };

  public getUserPreferences = (
    userId: UUID
  ): Promise<UserPreferences | undefined> => {
    const requestData: IRequest = {
      endpoint: `/account/users/${userId}/preferences`,
      method: HttpMethod.GET,
    };
    return this.requester
      .request(requestData)
      .then((response: AxiosResponse<UserPreferencesAPI>) =>
        userPreferencesTransformer(response.data)
      );
  };

  public getDefaultRoles = (): Promise<DefaultRole[]> => {
    const requestData: IRequest = {
      endpoint: '/account/default-roles',
      method: HttpMethod.GET,
    };
    return this.requester
      .request(requestData)
      .then((response: AxiosResponse<DefaultRolesAPI>) =>
        defaultRolesTransformer(response.data)
      );
  };

  public saveUserPreferences = (
    userId: UUID,
    userPreferences: UserPreferencesToSave
  ): Promise<UUID> => {
    const preferencesToSave = userPreferencesSaveForm(userPreferences);
    const requestData: IRequest = {
      endpoint: `/account/users/${userId}/preferences`,
      method: HttpMethod.POST,
      body: JSON.stringify(preferencesToSave),
    };

    return this.requester
      .request(requestData)
      .then((response: AxiosResponse<UserPreferencesAPI>) => response.data.id);
  };

  public updateUserPreferences = (
    userId: UUID,
    userPreferences: UserPreferences
  ): Promise<void> => {
    const preferencesToUpdate = userPreferencesSaveForm(userPreferences);
    const requestData: IRequest = {
      endpoint: `/account/users/${userId}/preferences/${userPreferences.id}`,
      method: HttpMethod.PUT,
      body: JSON.stringify(preferencesToUpdate),
    };

    return this.requester.request(requestData).then(undefined);
  };
}

export default UserRequester;
